import React from "react";
import { useAuth } from "../../context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

export default function ProfileIncompleteBanner() {
  const { user, isLoading, profileChecklist, refreshProfileChecklist } =
    useAuth();
  const [open, setOpen] = React.useState(false);
  const [checklistLoading, setChecklistLoading] = React.useState(true);
  const location = useLocation();

  const userId = user?.user_id;
  const checklist = profileChecklist;

  // Re-checked from the server every time this mounts (i.e. on every page
  // load/refresh), and can also be re-triggered live by other components
  // (e.g. MyProfile calling refreshProfileChecklist() after a save) via the
  // shared AuthContext state — no page reload needed either way.
  React.useEffect(() => {
    if (!userId) {
      setChecklistLoading(false);
      return;
    }

    let cancelled = false;
    setChecklistLoading(true);

    refreshProfileChecklist().finally(() => {
      if (!cancelled) setChecklistLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (isLoading || checklistLoading) return null;
  if (!userId) return null;

  // Build target path dynamically from logged-in user
  const targetPath = `/singles/my-profile/${userId}`;

  // Check if we are on the specific allowed page
  const isTargetPage = location.pathname === targetPath;

  if (!isTargetPage) return null;

  const profileIsComplete = checklist
    ? checklist.hasProfilePhoto &&
      checklist.hasGalleryPhotos &&
      checklist.hasAboutMe
    : false;

  const checklistItems = [
    {
      label: "Upload a clear profile photo",
      done: Boolean(checklist?.hasProfilePhoto),
    },
    {
      label: "Upload at least 3 photos to your gallery (MY GALLERY PHOTOS)",
      done: Boolean(checklist?.hasGalleryPhotos),
    },
    {
      label: "Write a short paragraph about yourself (ABOUT ME)",
      done: Boolean(checklist?.hasAboutMe),
    },
  ];

  // Render for COMPLETE profile
  if (profileIsComplete) {
    return (
      <div
        style={{
          position: "sticky",
          top: 76,
          zIndex: 20,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            pointerEvents: "auto",
            background: "#eaffea",
            padding: "12px 20px",
            border: "1px solid #b7eb8f",
            borderRadius: 14,
            width: "100%",
            maxWidth: 820,
            margin: "0 16px",
            display: "flex",
            justifyContent: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
            animation: "bannerFloatIn 0.5s ease-out",
          }}
        >
          <Link
            to="/singles/dashboard"
            style={{
              display: "inline-block",
              background: "#52c41a",
              color: "#ffffff",
              padding: "10px 24px",
              borderRadius: 10,
              fontWeight: 700,
              textDecoration: "none",
              boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
            }}
          >
            View All Singles →
          </Link>
        </div>
        <style>{`@keyframes bannerFloatIn { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    );
  }

  // Render for INCOMPLETE profile (accordion behavior)
  return (
    <div
      data-testid="profile-incomplete-banner"
      style={{
        position: "sticky",
        top: 76,
        zIndex: 20,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          background: "#ffefc1",
          padding: "16px 20px",
          border: "1px solid #f2c464",
          borderRadius: 14,
          width: "100%",
          maxWidth: 820,
          margin: "0 16px",
          fontSize: "14px",
          fontWeight: 500,
          textAlign: "left",
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          animation: "bannerFloatIn 0.5s ease-out",
        }}
      >
        <div
          onClick={() => setOpen((prev) => !prev)}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <strong
              style={{ fontSize: "20px", display: "block", marginBottom: 4 }}
            >
              Your profile is incomplete
            </strong>
            {!open && (
              <span style={{ fontSize: 13, color: "#6b5a00" }}>
                Click to see what you need to provide
              </span>
            )}
          </div>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#6b5a00",
              whiteSpace: "nowrap",
            }}
          >
            {open ? "Click here to collapse" : "Click here to open"}
          </span>
        </div>

        <div
          style={{
            maxHeight: open ? 520 : 0,
            overflow: "hidden",
            transition: "all 0.4s ease",
            opacity: open ? 1 : 0,
            marginTop: open ? 12 : 0,
          }}
        >
          <p style={{ margin: "6px 0 10px", fontWeight: 500 }}>
            You will not be able to view singles. To activate your profile,
            please complete the following:
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              margin: "4px 0 10px",
            }}
          >
            {checklistItems.map((item) => (
              <div
                key={item.label}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                {item.done ? (
                  <CheckCircleIcon
                    style={{ color: "#2e7d32", fontSize: 18, flexShrink: 0 }}
                  />
                ) : (
                  <CancelIcon
                    style={{ color: "#c62828", fontSize: 18, flexShrink: 0 }}
                  />
                )}
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 12,
              paddingTop: 8,
              borderTop: "1px dashed #e0b84f",
              fontSize: "13px",
              color: "#5f4b00",
            }}
          >
            <strong>Photo guidelines:</strong>
            <ul style={{ paddingLeft: 18, margin: "6px 0" }}>
              <li>No AI-generated photos</li>
              <li>No objects, animals, or scenery as main photo</li>
              <li>Face must be clearly visible</li>
              <li>Minimum size: 200 × 300 px</li>
              <li>No heavy filters or blurry images</li>
            </ul>
          </div>
        </div>
      </div>
      <style>{`@keyframes bannerFloatIn { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
